import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SectionCard } from "../../components/card";
import { PaymentMethod } from "../../components/payment-method";
import { Separator } from "../../components/separator";
import StageCFees from "./stage-c-fees";
import { P2Button, P2ButtonGroup, P2Checkbox, P2Modal, P2TextDescription } from "react-bootstrap-common-script";
import { useNavigate } from "react-router-dom";
import { PolymerAxios } from "../../utils/axios";
import { API_ApplicationCreditNotification, API_ApplicationPayment, API_ApplicationProcessPayment,API_EligibilityDebugLog, API_GetLoanStatus, API_OverrideEligibilityCheck, API_PaymentSuccess } from "../../utils/api";
import { useAppDispatch, useAppSelector } from "../../redux/hook";
import { StampDutyValuationEnum } from "../../models/enum/stamp-duty-valuation.enum";
import { HDBPayableFeeValuation, checkIsFlatResale } from "../../utils/form-helper";
import { ProcessPayment } from "../../models/classes/stage-c/Process-Payment.class";
import { PaymentMethodEnum } from "../../models/enum/payment-method.enum";
import moment from "moment";
import { LottieLoader } from "../../components/loader";
import { LoanTypeEnum } from "../../models/enum/loan-type.enum";
import config from "../../config";
import useGetEligibilityOverride from "../../hooks/use-get-eligibility-override";
import { EligibilityCheck, EligibilityOverride } from "../../models/classes/EligibilityOverride.class";
import { updateFormData } from "../../redux/slices/application-start-slice";
import { ApplicationStart } from "../../models/classes/ApplicationStart.class";
import { Properties, PropertyFinancingInfo } from "../../models/polymer/Properties.model";
import { customWindow } from "../../models/interfaces/wogaa.interface";
import { stageCTrackingId } from "../../utils/wogaa-ids";

const StageCMainColumn = () => {
  
  const AppDispatch = useAppDispatch();
  const queryParameters = new URLSearchParams(window.location.search)
  const isDebug = queryParameters.get("debug");
  const application_id = queryParameters.get("application_id");
  const application_uuid = useAppSelector(state => state.app.ApplicationIdSlice.applicationId);
  const [termsCheckBox, setTermsCheckBox] = useState(false);
  const [isPaymentLoading, setPaymentLoading] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<string>(PaymentMethodEnum.card);
  const [continueEnabled, setContinueEnabled] = useState(false);
  const [totalPaymentAmount, setTotalPaymentAmount] = useState<number>(0);
  const [valuationFee, setValuationFee] = useState<number>(0);
  const startApplicationFormData = useAppSelector(state => state.app.ApplicationStartSlice.formData);
  const applicationStep2FormData = useAppSelector(state => state.app.ApplicationStep2Slice.formData);
  const applicationStep3FormData = useAppSelector(state => state.app.ApplicationStep3Slice.formData);
  const salesTypeCode = startApplicationFormData?.selected_hdb_property?.financing_info?.sales_type_code;
  const [isLoading, setIsLoading] = useState<boolean>(salesTypeCode === undefined);
  const { bypassPayments } = useGetEligibilityOverride(startApplicationFormData.selected_hdb_property?.hdb_ref);

  const paymentFormRef = useRef<HTMLFormElement>(null); 

  const isProd = useMemo(()=>{
    console.log("process.env.NODE_ENV",process.env.NODE_ENV);
    if (process.env.NODE_ENV !== "production" || document.location.origin === "https://services3-dev.hdb.gov.sg" || document.location.origin === "https://services3-stg.hdb.gov.sg") {
      return false;
    }
    else{
      return true;
    }
  },[])

  const loginUser = useAppSelector(state => state.auth.AuthenticationSlice.loginUser);
  let navigate = useNavigate();
  const handlePaymentHandler = useCallback((value: string) => {
    setPaymentMethod(value);
    updateContinueButton(value, termsCheckBox);
  },[termsCheckBox]);
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);


  const handleTermsCheckboxToggle = useCallback(() => {
    setTermsCheckBox((value) => !value);
    updateContinueButton(paymentMethod, !termsCheckBox);
  },[paymentMethod, termsCheckBox]);

  const updateContinueButton = (paymentMethod: string, termsChecked: boolean) => {
    setContinueEnabled(!!paymentMethod && termsChecked);
  };

  const isToIncludeValuation = useMemo(()=>{
    const isProposedHDBLoan = applicationStep2FormData.new_loan_type === LoanTypeEnum.hdb_loan;
    const isResale = checkIsFlatResale(startApplicationFormData.selected_hdb_property?.financing_info.sales_type_code!);
    const isValuationHDB = applicationStep3FormData.valuation === StampDutyValuationEnum.hdb;
    
    console.log("DEBUG -- isValuationHDB", isValuationHDB, isResale, isProposedHDBLoan);

    if(isValuationHDB || (isResale && isProposedHDBLoan)){
      return true;
    }
    return false
  },[applicationStep2FormData.new_loan_type, applicationStep3FormData.valuation, startApplicationFormData.selected_hdb_property?.financing_info.sales_type_code]);
  
  useEffect(() => {
    if(!application_id || application_id == "undefined" || application_id !== application_uuid){
      window.location.href = "/transfer/dashboard";
    }
  }, [application_id])

  useEffect(() => {
    if(salesTypeCode === undefined && startApplicationFormData?.selected_hdb_property?.hdb_ref) {
      PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_GetLoanStatus, 
        {
          hdb_ref: startApplicationFormData?.selected_hdb_property?.hdb_ref,
        }).then((data) => {
          const updatedStartApplicationForm = new ApplicationStart({...startApplicationFormData, selected_hdb_property: new Properties({...startApplicationFormData.selected_hdb_property, financing_info: new PropertyFinancingInfo({...startApplicationFormData.selected_hdb_property?.financing_info, sales_type_code: data.data.sales_type_code})}) });
          AppDispatch(updateFormData(updatedStartApplicationForm));
          setIsLoading(false);
        }).catch((error) => {
          alert("An error occurred. Please try again later.");
          window.location.href = "/transfer/dashboard";
        })
    }
  }, [salesTypeCode, startApplicationFormData])

  useEffect(() => {
    if(!startApplicationFormData?.selected_hdb_property?.hdb_ref) {
      window.location.href = "/transfer/dashboard";
    }
  }, [startApplicationFormData])

  const performByPassPayment = useCallback( async (paymentResponse:any) => {
    const creditNotificationBody: CreditNotificationBody = {
      systIdforPayment: "AC89598",
      numTrnscn: 0,
      totalAmt: 0,
      ttcNum: paymentResponse?.data?.ttcNum, // must euqal with paymentRequest ttcNum
      hashNum: "HASH123",
      transactionDate: moment().format('yyyy-MM-DD'),
      transactionTime: moment().format('HH:mm:ss'),
      details: []
    };

    if (!bypassPayments.admin) {
      creditNotificationBody.numTrnscn += 2;
      creditNotificationBody.totalAmt += 50;
      creditNotificationBody.details = creditNotificationBody.details.concat([
        {
          bankReferenceNo: "REF123",
          receiptNum: "REC123",
          caseNum: "141104S0235",
          systID: "SYS123",
          amount: 45.87,
          rcptngCode: "0875C"
        },
        {
          bankReferenceNo: "REF123",
          receiptNum: "REC123",
          caseNum: "141104S0235",
          systID: "SYS123",
          amount: 4.13,
          rcptngCode: "6225B"
        }
      ])
    }

    if (!bypassPayments.valuation && isToIncludeValuation) {
      creditNotificationBody.numTrnscn += 4;
      creditNotificationBody.totalAmt += 248.90;
      creditNotificationBody.details = creditNotificationBody.details.concat([
        {
          bankReferenceNo: "REF123",
          receiptNum: "REC123",
          caseNum: "141104S0235",
          systID: "SYS123",
          amount: 18.35,
        },
        {
          bankReferenceNo: "REF123",
          receiptNum: "REC123",
          caseNum: "141104S0235",
          systID: "SYS123",
          amount: 1.65,
        },
        {
          bankReferenceNo: "REF123",
          receiptNum: "REC123",
          caseNum: "141104S0235",
          systID: "SYS123",
          amount: 210,
        },
        {
          bankReferenceNo: "REF123",
          receiptNum: "REC123",
          caseNum: "141104S0235",
          systID: "SYS123",
          amount: 18.9,
        }
      ])
    }

    const paymentSuccessRequest = new URLSearchParams();
    paymentSuccessRequest.append("ttcNumSuccess", paymentResponse?.data?.ttcNum ?? "");

    try{
      const creditNotiResponse = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_ApplicationCreditNotification, creditNotificationBody);
      const paymentSuccessResponse = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken, "application/x-www-form-urlencoded").post(API_PaymentSuccess, paymentSuccessRequest)

      if(paymentResponse?.data?.status){
        setPaymentLoading(false);
        navigate(`/application-coo/stage-c/payment/successful?application_id=${application_uuid}`)
      }
      else{
        setPaymentLoading(false);
        navigate(`/application-coo/stage-c/payment/unsuccessful?application_id=${application_uuid}`)
      }
    }catch(e){
      setPaymentLoading(false);
    }


  },[application_uuid, isToIncludeValuation, loginUser?.uinfin, navigate, singpassToken, bypassPayments])

  const continuePayment = useCallback(async () => {
    if (customWindow.wogaaCustom) {
      console.log('Start wogaa stage C');
      customWindow.wogaaCustom.startTransactionalService(stageCTrackingId);
    }
    setPaymentLoading(true);
    let processPaymentObj = new ProcessPayment();
    processPaymentObj.setApplicationUUID(application_uuid ?? "");
    processPaymentObj.setPaymentMode(paymentMethod as PaymentMethodEnum);

    const overrideEligibilityCheck = new EligibilityOverride({ application_uuid: application_id });
    
    if (bypassPayments.admin) {
      overrideEligibilityCheck.administrative_fee = EligibilityCheck.BYPASS;
    } else {
      overrideEligibilityCheck.administrative_fee = EligibilityCheck.MUST_CHECK;
      processPaymentObj.addTransactionType("admin");
    }

    if (bypassPayments.valuation) {
      overrideEligibilityCheck.valuation_fee = EligibilityCheck.BYPASS;
    } else if (!isToIncludeValuation) {
      overrideEligibilityCheck.valuation_fee = EligibilityCheck.EXEMPT;
    } else {
      overrideEligibilityCheck.valuation_fee = EligibilityCheck.MUST_CHECK;
      processPaymentObj.addTransactionType("valuation")
    }

    try {
      let processPaymentBody = {
        application_uuid: application_uuid,
      }
      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_ApplicationProcessPayment, processPaymentBody);

      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_OverrideEligibilityCheck, overrideEligibilityCheck);

      const paymentResponse = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_ApplicationPayment, processPaymentObj.json());

      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_EligibilityDebugLog, {
        applicationUuid: loginUser?.uinfin,
        message: `FE STAGE C DEBUG LOG CATCH --- Payment url from API : ${paymentResponse?.data?.paymentUrl} | ${paymentResponse?.data?.ttcNum} | ${paymentResponse?.data?.systIdTagPayReq}`
      });

      document.getElementById("ttcNumPayReq")?.setAttribute('value', paymentResponse?.data?.ttcNum);
      document.getElementById("systIdTagPayReq")?.setAttribute('value', paymentResponse?.data?.systIdTagPayReq);
      document.getElementById("systIdforPaymentPayReq")?.setAttribute('value', "BR20");
      setPaymentLoading(false);
      // skip payment gateway if the transaction amount if empty
      if (totalPaymentAmount !== 0 && isDebug !== "true") {
        paymentFormRef.current?.submit();
      }

    } catch (error) {
      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_EligibilityDebugLog, {
        uinfin: loginUser?.uinfin,
        message: `FE STAGE C DEBUG LOG CATCH --- ${error}`
      });

      alert("Oops, something wrong with payment. Please try again.")

      setPaymentLoading(false);
    }

  },[application_uuid, paymentMethod, isToIncludeValuation, loginUser?.uinfin, singpassToken, isDebug, bypassPayments, totalPaymentAmount, customWindow.wogaaCustom])

  const byPassPayment = useCallback(async()=>{
    setPaymentLoading(true);
    let processPaymentObj = new ProcessPayment();
    processPaymentObj.setApplicationUUID(application_uuid ?? "");
    processPaymentObj.setPaymentMode(paymentMethod as PaymentMethodEnum);
    
    const overrideEligibilityCheck = new EligibilityOverride({ application_uuid: application_id });
    
    if (bypassPayments.admin) {
      overrideEligibilityCheck.administrative_fee = EligibilityCheck.BYPASS;
    } else {
      overrideEligibilityCheck.administrative_fee = EligibilityCheck.MUST_CHECK;
      processPaymentObj.addTransactionType("admin");
    }

    if (bypassPayments.valuation) {
      overrideEligibilityCheck.valuation_fee = EligibilityCheck.BYPASS;
    } else if (!isToIncludeValuation) {
      overrideEligibilityCheck.valuation_fee = EligibilityCheck.EXEMPT;
    } else {
      overrideEligibilityCheck.valuation_fee = EligibilityCheck.MUST_CHECK;
      processPaymentObj.addTransactionType("valuation")
    }

    try {
      let processPaymentBody = {
        application_uuid: application_uuid,
      }

      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_ApplicationProcessPayment, processPaymentBody);

      await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_OverrideEligibilityCheck, overrideEligibilityCheck);

      const paymentResponse = await PolymerAxios(loginUser?.uinfin ?? "", singpassToken).post(API_ApplicationPayment, processPaymentObj.json());

      performByPassPayment(paymentResponse);
    } catch (error) {
      console.log(error)
      alert("Oops, something wrong with bypassing. Please try again.")
      setPaymentLoading(false);
    }
  },[application_uuid, isToIncludeValuation, loginUser?.uinfin, paymentMethod, performByPassPayment, singpassToken])

  const navigateBack = useCallback(()=>{
    navigate(-1)
  },[navigate])

  useEffect(() => {
    updateContinueButton(paymentMethod, termsCheckBox);

  }, [paymentMethod, termsCheckBox]);

  useEffect(() => {
      let totalAmount: number = 0;
      if(!bypassPayments.admin){
        totalAmount += 50;
      }

      if (isToIncludeValuation){
        const totalValuationFee = Number(HDBPayableFeeValuation(startApplicationFormData?.selected_hdb_property?.flat_type, applicationStep3FormData?.valuation as StampDutyValuationEnum || undefined));
        setValuationFee(totalValuationFee);

        if (!bypassPayments.valuation) totalAmount += totalValuationFee;
      }

      setTotalPaymentAmount(totalAmount);
  }, [applicationStep3FormData, isToIncludeValuation, startApplicationFormData, bypassPayments])

  const paymentUrl = useMemo(() => {    
    if (document.location.origin === "https://services3.hdb.gov.sg") {
      return config.prodPaymentURL;
    }
    else{
      return config.stagingPaymentURL;
    }
  },[])

  return (
    <>
      <form ref={paymentFormRef} method="POST" action={paymentUrl}>
        <input type={isDebug === "true" ? "" :"hidden"} name="ttcNumPayReq" id="ttcNumPayReq" value={""}/>
        <input type={isDebug === "true" ? "" :"hidden"} name="systIdforPaymentPayReq" id="systIdforPaymentPayReq" value={"BR20"}/>
        <input type={isDebug === "true" ? "" :"hidden"} name="systIdTagPayReq" id="systIdTagPayReq" value={""}/>
        {isDebug === "true" && <input type="submit"/>}
      </form>
      <div className="d-flex justify-content-center">
        <SectionCard
          required={false}
          className="flex-fill polymer-payment-section"
          titleChildren={<P2TextDescription title={"Payment"} />}
          contentChildren={
            isLoading ? 
              <div>
                <LottieLoader />
              </div> :
              <div className="polymer-main-column-container">
              <p className="polymer-main-column-header">Please note the following fees required for HDB to process your application.</p>
              <StageCFees valuationFee={valuationFee} totalFee={totalPaymentAmount} bypassAdminFee={bypassPayments.admin} bypassValuationFee={bypassPayments.valuation}/>
              <PaymentMethod PaymentHandler={handlePaymentHandler} />
              <div className="polymer-main-column-spacer">
                <Separator />
              </div>
              <div className="polymer-main-column-tnc">
                <p className="polymer-main-column-tnc-title">Terms & Conditions:</p>
                <p className="polymer-main-column-tnc-content">Please note that all fees paid are non-refundable in the event of any cancellation or rejection of the application. This includes the cancellation of the application should submission of information or documents are not provided within the specified time frame which deem the application as incomplete, or incorrect. For updates on the application progress, you may check our progress tracker once the application is submitted successfully.</p>
                <P2Checkbox
                  isActive={termsCheckBox}
                  onClick={handleTermsCheckboxToggle}
                  description={
                    <p className="m-0 polymer-main-column-tnc-checkbox">
                      I have read and understood the above Terms & Conditions.
                    </p>
                  }
                />
              </div>
            </div>
          }
        />
      </div>
      
      <Separator style={{margin:"32px 0"}}/>
      <div className='polymer-application-coo__card-button'>
        <P2ButtonGroup>
          <P2Button variant={"outline-primary"} onClick={navigateBack}>Cancel</P2Button>
          <P2Button variant={"primary-1"} onClick={continuePayment} disabled={!continueEnabled}>Continue</P2Button>
          {!isProd && <P2Button variant={"primary-1"} onClick={byPassPayment} disabled={!continueEnabled}>ByPass Payment</P2Button>}
        </P2ButtonGroup>
      </div>
      <P2Modal
        isOpen={isPaymentLoading}
        children={
          <LottieLoader/>
        }
    />
    </>
  );
};

export default StageCMainColumn;

type CreditNotificationBody = {
  systIdforPayment: string;
    numTrnscn: number;
    totalAmt: number;
    ttcNum: any;
    hashNum: string;
    transactionDate: string;
    transactionTime: string;
    details: {
      bankReferenceNo: string;
      receiptNum: string;
      caseNum: string;
      systID: string;
      amount: number;
      rcptngCode?: string;
    }[];
}
